import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Player, Language } from '../../types';
import { getTeamName } from '../../services/teamService';
import { t } from '../../i18n';

interface PlayerHeaderProps {
    player: Player;
    lang: Language;
    onBack: () => void;
}

const getCountryCode = (nationality?: string) => {
    if (!nationality) return 'un';
    const n = nationality.trim();
    const map: Record<string, string> = {
        'Spain': 'es',
        'England': 'gb-eng',
        'Scotland': 'gb-sct',
        'Wales': 'gb-wls',
        'Germany': 'de',
        'France': 'fr',
        'Italy': 'it',
        'Brazil': 'br',
        'Argentina': 'ar',
        'Portugal': 'pt',
        'Netherlands': 'nl',
        'Belgium': 'be',
        'Norway': 'no',
        'Sweden': 'se',
        'Korea, South': 'kr',
        'Japan': 'jp',
        'USA': 'us',
        'Poland': 'pl',
        'Turkey': 'tr',
        'Morocco': 'ma',
        'Croatia': 'hr',
        'Denmark': 'dk',
        'Egypt': 'eg',
        'Senegal': 'sn',
        'Uruguay': 'uy',
        'Colombia': 'co',
        'Nigeria': 'ng',
        'Ghana': 'gh',
        'Ivory Coast': 'ci',
        'Cameroon': 'cm',
        'Austria': 'at',
        'Switzerland': 'ch',
        'Ukraine': 'ua',
        'Serbia': 'rs',
        'Czech Republic': 'cz',
        'Slovakia': 'sk',
        'Slovenia': 'si',
        'Hungary': 'hu',
        'Romania': 'ro',
        'Bulgaria': 'bg',
        'Greece': 'gr',
        'Ireland': 'ie',
        'Northern Ireland': 'gb-nir',
        'Finland': 'fi',
        'Iceland': 'is',
        'Mexico': 'mx',
        'Canada': 'ca',
        'Australia': 'au',
        'New Zealand': 'nz',
        'Chile': 'cl',
        'Ecuador': 'ec',
        'Paraguay': 'py',
        'Peru': 'pe',
        'Venezuela': 've',
        'Algeria': 'dz',
        'Tunisia': 'tn',
        'Mali': 'ml',
        'Guinea': 'gn',
        'Burkina Faso': 'bf',
        'DR Congo': 'cd',
        'Congo': 'cg',
        'South Africa': 'za',
        'Gabon': 'ga',
        'Zambia': 'zm',
        'Iran': 'ir',
        'Saudi Arabia': 'sa',
        'United Arab Emirates': 'ae',
        'Qatar': 'qa',
        'Iraq': 'iq'
    };
    return map[n] || 'un';
};

export const PlayerHeader: React.FC<PlayerHeaderProps> = ({ player, lang, onBack }) => {
    const countryCode = getCountryCode(player.nationality?.split(',')[0]);
    const flagUrl = `https://flagcdn.com/w80/${countryCode}.png`;

    return (
        <div className="relative bg-white pb-16 pt-0 overflow-hidden">
            {/* Ambient Watermark */}
            <div className="absolute -top-16 -right-24 w-[400px] h-[400px] md:w-[500px] md:h-[500px] pointer-events-none opacity-[0.07] transform -rotate-12 select-none">
                <img 
                    src={player.teamLogo} 
                    alt="" 
                    className="w-full h-full object-contain" 
                />
            </div>

            <div className="relative z-10 flex flex-col items-center text-center px-6 pt-24">
                <div className="relative mb-8">
                    <div className="w-32 h-32 md:w-36 md:h-36 rounded-full border border-gray-100 bg-gray-50 p-1 overflow-hidden transition-transform duration-500 md:hover:scale-105 shadow-xl shadow-slate-100">
                        <img 
                            src={player.avatar} 
                            alt={player.name} 
                            className="w-full h-full object-cover rounded-full" 
                            onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?background=f1f5f9&color=cbd5e1&bold=true&name=${player.name}`; }}
                        />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-9 h-9 md:w-10 md:h-10 bg-white p-1 rounded-xl border border-gray-100 shadow-lg flex items-center justify-center overflow-hidden">
                        <img 
                            src={flagUrl} 
                            alt={player.nationality} 
                            className="w-full h-full object-cover rounded-lg" 
                            onError={(e) => { e.currentTarget.src = 'https://flagcdn.com/w80/un.png'; }}
                        />
                    </div>
                </div>
                
                <div className="mb-10">
                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter mb-3 uppercase">
                        {player.name}
                    </h1>
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-rose-500 font-black text-[10px] uppercase tracking-widest">
                            {getTeamName(player.currentTeamId, lang, player.team)}
                        </span>
                        <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                        <span className="text-slate-400 font-black text-[10px] uppercase tracking-widest">
                            {t(lang, player.position.toLowerCase()) || player.position}
                        </span>
                    </div>
                </div>

                <div className="w-full max-w-lg grid grid-cols-3 gap-6">
                    {[
                        { label: t(lang, 'playerAge'), value: player.age || '-' },
                        { label: t(lang, 'playerHeight'), value: `${player.height || '-'} cm` },
                        { label: t(lang, 'playerWeight'), value: `${player.weight || '-'} kg` }
                    ].map((item, idx) => (
                        <div key={idx} className="flex flex-col items-center justify-center group transition-colors">
                            <span className="block text-[8px] text-slate-300 font-black uppercase tracking-[0.2em] mb-1.5">{item.label}</span>
                            <span className="block text-sm md:text-base font-black text-slate-900 uppercase tracking-tight">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
